import { faker } from "@faker-js/faker";
import { AxiosResponse } from "axios";
import Decimal from "decimal.js";
import { get, isEmpty, isEqual, orderBy, set, uniqBy } from "lodash";
import moment, { Moment } from "moment";

import {
    BinarySensorsCLSList,
    DATE_MONTH_YEAR_FORMAT,
    DefaultMaxCharacter,
    DefaultMinCharacter,
    HttpStatus,
    Patterns,
} from "constant";
import { MAX_CHART_VALUE_CHARS } from "constant/chart";
import { Dashboard } from "generated/models/Dashboard";
import LocalStorageService from "service/localStorageService";
import { DashboardTagOptions, ResponseDto } from "types";
import { NullableNumber } from "types/Reports";
import { LdsApp } from "types/Ldsu";

const PERCENTAGE_ACCURACY = 1;

export const orgIdQuery = () => {
    return {
        organization_id: orgId(),
    };
};

export const orgId = () => {
    return LocalStorageService.getItem("orgId");
};

export function createResponseStandard<T = any>(
    res: AxiosResponse
): ResponseDto<T> {
    return {
        status: res.status,
        data: res.data.data || res.data,
        message: res?.data?.message,
        total: res?.data?.total,
    };
}

export function createErrorResponseStandard<T = any>(
    error: any
): ResponseDto<T> {
    return {
        status: error.response
            ? error.response.status
            : HttpStatus.INTERNAL_SERVER_ERROR,
        data: error.response ? error.response.data : null,
        error,
        message:
            get(error, "response.data.message") ??
            get(error, "response.data.description", null),
    };
}

/** Coerces device/API fields (number or numeric string) to a finite number; otherwise null. */
export const toFiniteReadingNumber = (value: unknown): number | null => {
    if (value === undefined || value === null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** Returns true when the device is a binary (0/1) sensor based on its CLS class or data format. */
export const isBinarySensor = (cls: string, format?: string): boolean =>
    BinarySensorsCLSList.includes(cls) || format === "boolean";

/** Converts a raw numeric reading to 0 or 1 for binary sensors. */
export const toBinaryValue = (value: number): number => {
    if (Number.isNaN(value)) {
        return value;
    }

    return value > 0 ? 1 : 0;
};

/**
 * Create status for created property
 * @param { number } dateNumber - Date in number
 * @param { string } dateFormat - Date format (default: "DD/MM/YYYY")
 * @returns { string }
 */
export const formatDate = (
    dateNumber: number | string | Date,
    dateFormat = DATE_MONTH_YEAR_FORMAT
): string => {
    if (!dateNumber) return "-";
    return moment
        .utc(dateNumber)
        .utcOffset(moment().utcOffset())
        .format(dateFormat);
};

export const tagOptions = (dashboards: Dashboard[]): DashboardTagOptions[] => {
    return dashboards.map(({ uuid, name, icon_color }: Dashboard) => ({
        key: uuid,
        text: name,
        value: name,
        label: {
            color:
                get(icon_color, "[0]", "") === "#"
                    ? icon_color
                    : `#${icon_color}`,
            empty: false,
            circular: true,
        },
    }));
};

/**
 * Convert number of minutes to hours for gateway charts
 * @param { any } timeValue  - Number of minutes
 * @returns { string } - In two decimal place.
 */
export const convertMinutesToHours = (timeValue: any) => {
    let hours: any = Number(timeValue) / 60;
    if (Number(hours) % 1 !== 0) {
        hours = hours.toFixed(2);
    }
    return String(hours);
};

export const formatUnitWithSymbol = (unit: any) => {
    if (unit === "C") {
        return `°C`;
    }
    return unit;
};

export const renderCountdown = (countdown: any) => {
    const mins = Math.floor(countdown / 60);
    const secs = countdown % 60;

    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

export const combineResultArrays = (res: any) => {
    if (res) {
        const combinedValues = res?.data?.data?.result?.reduce(
            (acc: any, curr: any) => {
                acc.push(...curr.values);
                return acc;
            },
            []
        );

        combinedValues.sort((a: any, b: any) => {
            return a[0] - b[0];
        });

        const uniqueCombinedValues: any = [];
        const timestamps: any = [];
        for (const value of combinedValues) {
            if (!timestamps.includes(value[0])) {
                timestamps.push(value[0]);
                uniqueCombinedValues.push([value[0], value[1]]);
            }
        }

        set(res, "data.data.result[0].values", uniqueCombinedValues);
    }
};

export const getPrimaryUnit = (appSensor: any) => {
    for (const unit of appSensor?.UNITS || []) {
        if (unit === "BINARY") return "";
        if (appSensor?.[unit]?.["PRI"] === "TRUE") {
            return appSensor[unit]["SYMBOL"];
        }
    }
};
export const getPrimaryUnitKey = (appSensor: any) => {
    for (const unit of appSensor.UNITS || []) {
        if (appSensor[unit]["PRI"] === "TRUE") {
            return unit.toLowerCase();
        }
    }
};

export const getUnitSymbolArray = (appSensor: any) => {
    return appSensor.UNITS.reduce((acc: any, unitName: string) => {
        if (["BINARY", "COMPASS"].includes(unitName)) {
            acc.push("");
        } else {
            acc.push(appSensor[unitName].SYMBOL);
        }
        return acc;
    }, []);
};

/**
 * Format tick label with appropriate precision and abbreviation.
 * Scaling is gated by integer-digit length: only values with > 5 integer
 * digits get K/M/B scaling.
 * - integer-digit length ≤ 5 → no scaling
 * - >= 1,000,000,000           → "1.5B"
 * - >= 1,000,000               → "2.0M"
 * - otherwise (6+ digits)      → "123.5K"
 */
export const formatTickLabel = (value: number, accuracy: number): string => {
    if (value === 0) {
        return accuracy > 0 ? `0.${"0".repeat(accuracy)}` : "0";
    }

    const absValue = Math.abs(value);
    const integerDigitLength = Math.floor(absValue).toString().length;

    if (integerDigitLength <= 5) {
        return new Decimal(value).toFixed(accuracy);
    }

    if (absValue >= 1000000000) {
        const scaled = value / 1000000000;
        return `${new Decimal(scaled).toFixed(accuracy)}B`;
    }
    if (absValue >= 1000000) {
        const scaled = value / 1000000;
        return `${new Decimal(scaled).toFixed(accuracy)}M`;
    }
    const scaled = value / 1000;
    return `${new Decimal(scaled).toFixed(accuracy)}K`;
};

export const addDecimalPlace = (data: any, accuracy: any) => {
    let numValue = Number.parseFloat(data);
    if (Number.isNaN(numValue)) {
        numValue = Number.parseFloat("0");
    }

    // Handle invalid accuracy values - default to 0
    let parsedAccuracy = Number.parseInt(accuracy);
    if (Number.isNaN(parsedAccuracy) || parsedAccuracy < 0) {
        parsedAccuracy = 0;
    }

    // Use Decimal.js to handle precision issues
    const roundedValue = new Decimal(numValue).toDecimalPlaces(parsedAccuracy);

    // Normalize negative zero (e.g. "-0.0000") to "0.0000"
    if (roundedValue.isZero()) {
        return new Decimal(0).toFixed(parsedAccuracy);
    }

    return roundedValue.toFixed(parsedAccuracy);
};

export const formatNullableNumber = (n: number | null, accuracy: string): string => {
    return n === null || n === undefined ? "-" : addDecimalPlace(n, accuracy);
};

export const getDaysInMonths = (month: any) => {
    const date = new Date();
    const currentYear = date.getFullYear();
    return new Date(currentYear, month, 0).getDate();
};

export const getPrimaryUnitName = (appSensor?: LdsApp) => {
    if (!appSensor?.UNITS) return "";
    for (const unit of appSensor?.UNITS || []) {
        if (appSensor?.[unit]?.["PRI"] === "TRUE") {
            return appSensor[unit];
        }
    }
    return "";
};
declare global {
    interface String {
        fill: (data: { [key: string]: any }) => string;
        capitalize: () => string;
        isNumeric: () => boolean;
        toNumFixedDown(digits: number): string;
        toHex: () => string;
    }

    interface Number {
        toMoment(): moment.Moment;
    }

    interface Array<T> {
        pluck: (key: string, fields?: string[]) => { [key: string]: T };
    }

    var getId: () => string;

    var objectFluent: (obj: any, data: any) => any;

    interface HTMLFormElement {
        toData: <T = {}>() => T;
    }
}

// eslint-disable-next-line no-extend-native
String.prototype.fill = function (data) {
    // @ts-ignore
    const str = this.replaceAll(/\{\w+\}/g, function (matched) {
        const key = matched.replace("{", "").replace("}", "");
        return get(data, key, matched);
    });

    return String(str);
};

// eslint-disable-next-line no-extend-native
String.prototype.capitalize = function () {
    return (
        this.toLowerCase().charAt(0).toUpperCase() + this.toLowerCase().slice(1)
    );
};

// eslint-disable-next-line no-extend-native
Array.prototype.pluck = function (key: string, fields?: string[]) {
    return this.reduce((acc: any, curr: any) => {
        if (fields) {
            if (fields.includes(curr[key])) {
                acc[curr[key]] = curr;
            }
        } else {
            acc[curr[key]] = curr;
        }
        return acc;
    }, {});
};

globalThis.getId = () => faker.datatype.uuid();

globalThis.objectFluent = (obj: any, data: any) => {
    for (let key in obj) {
        if (typeof obj[key] === "function") {
            obj[key] = obj[key](data[key], data);
        } else {
            obj[key] = data[key] ?? obj[key];
        }
    }

    return obj;
};

// eslint-disable-next-line no-extend-native
String.prototype.isNumeric = function () {
    if (typeof this != "string") return false;
    return (
        !Number.isNaN(Number(this)) && !Number.isNaN(Number.parseFloat(this))
    );
};

declare module "moment" {
    interface Moment {
        toSeconds(): number;
    }
}

// eslint-disable-next-line no-extend-native
Number.prototype.toMoment = function () {
    return moment(
        `${Math.floor(Number(this) / 3600)}:${Math.floor(
            (Number(this) % 3600) / 60
        )}:${(Number(this) % 3600) % 60}`,
        "HH:mm:ss"
    );
};

// https://stackoverflow.com/a/4912870/7746224
// eslint-disable-next-line no-extend-native
String.prototype.toNumFixedDown = function (digits = 0) {
    const re = new RegExp(
        String.raw`([-]?\d+\.\d{` + digits + String.raw`})(\d)`
    ),
        m = re.exec(this.toString());
    return (m ? m[1] : this.valueOf()).replaceAll(/^0+\B/g, "");
};

// eslint-disable-next-line no-extend-native
moment.prototype.toSeconds = function () {
    return this.hours() * 60 * 60 + this.minutes() * 60 + this.seconds();
};

export const humanReadableFileSize = (bytes: any, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + " B";
    }

    const units = si
        ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
        : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (
        Math.round(Math.abs(bytes) * r) / r >= thresh &&
        u < units.length - 1
    );

    return bytes.toFixed(dp) + " " + units[u];
};

export const getFirstSubscriptionInfo = (subscriptionList: any) => {
    const firstSub = orderBy(subscriptionList, ["create_time"], ["asc"])[0];
    if (firstSub) {
        const firstSubDate: any = new Date(firstSub.create_time);
        return {
            startMonth: firstSubDate.getMonth() + 1,
            startYear: firstSubDate.getFullYear(),
        };
    }
    return {
        startMonth: 1,
        startYear: moment().year(),
    };
};

export const calcPercentDiff = (curr: number, prev: number) => {
    if (curr === 0 && prev === 0) return 0;
    if (prev === 0) return 100;
    return ((curr - prev) / prev) * 100;
};

export const compareNumber = (
    totalValue: number | null,
    previousValue: number | null,
) => {
    // Case 2: If there is no metric for any one of the periods, don't show percentage
    if (previousValue === null || totalValue === null) {
        return null;
    }

    // Assuming calculations before this point to be accurate even when using IEEE 754 numbers
    // Converting to Decimal to handle edge cases (e.g. previous = 100, current = 105.05)
    const previous = new Decimal(previousValue);
    const current = new Decimal(totalValue);

    // Case 3: If current equals previous
    if (previous.cmp(current) === 0) {
        return {
            percentage: new Decimal(0),
            percentageToShow: (new Decimal(0)).toFixed(
                PERCENTAGE_ACCURACY,
                Decimal.ROUND_HALF_UP
            ),
            direction: "equal",
        }
    }

    let percentage: Decimal;
    // Case 1a & 1b: Handle zero cases
    if (previous.cmp(0) === 0 || current.cmp(0) === 0) {
        // If previous is 0 and current is not 0 -> 100% increase
        // If current is 0 and previous is not 0 -> 100% decrease
        percentage = new Decimal(100);
    } else {
        // Normal case: calculate percentage difference
        // Using the original values without limiting decimal places for calculation
        percentage = current.sub(previous).div(previous.abs()).mul(100).abs();
    }

    // Round to specified accuracy only for display
    const roundedPercentage = percentage.toDP(
        PERCENTAGE_ACCURACY,
        Decimal.ROUND_HALF_UP
    );

    const percentageStr = roundedPercentage.toFixed(
        PERCENTAGE_ACCURACY,
        Decimal.ROUND_HALF_UP
    );

    return {
        percentage,
        percentageToShow: percentageStr,
        direction: current.lessThan(previous) ? "decrease" : "increase",
    };
}

export const isValidValueInForm = (
    path: string,
    data: any,
    defaultData: any,
    regex?: any
) => {
    const text = get(data, path, "");
    const defaultValue = get(defaultData, path, "");
    const textTrim = text.trim();
    const textLength = text.length;

    return (text && isEqual(defaultValue, text)) || regex
        ? regex.test(textTrim)
        : textLength >= DefaultMinCharacter &&
        textLength <= DefaultMaxCharacter;
};

export const checkIfReachable = (
    lastTimestamp: any,
    reportingIntvl: any = 5
) => {
    // If checking GW, reporting intvl is 60
    // If checking LDSU, use reporting intvl from config API, by default is 5

    const now = moment().unix();
    const n = 1.5;
    const offset = 30;

    if (now - lastTimestamp <= reportingIntvl * n + offset) {
        return true;
    }
    return false;
};

export const isSensor = (CLS: number) => CLS < 32768;
export const isActuator = (CLS: number) => CLS >= 32768;

// if CLS < sensor range => sensor
// if CLS > sensor range => actuator
// Current actuator are all 32768
export const clsToAttributeTable = () => {
    let clsNameObj: any = {};
    for (const [key, value] of Object.entries(
        LocalStorageService.getItem("metricInfo").cls
    )) {
        clsNameObj[key] = (value as any).name;
    }
    return clsNameObj;
};

// eslint-disable-next-line no-extend-native
HTMLFormElement.prototype.toData = function () {
    const data: any = {};
    const formData = new FormData(this);
    formData.forEach((value, key) => {
        data[key] = value;
    });

    return data;
};

// eslint-disable-next-line no-extend-native
String.prototype.toHex = function () {
    let hex, i;

    let result = "";
    for (i = 0; i < this.length; i++) {
        hex = this.charCodeAt(i).toString(16);
        result += ("000" + hex).slice(-4);
    }

    return result;
};

export const isHttpSuccess = (status: number) => status >= 200 && status < 300;

export const parseColorToHex = (color: string) => {
    let hexColor = color;
    if (color?.length === 9) {
        const a = color.substring(1, 3);
        hexColor = `#${color.substring(3)}${a}`;
    }

    return hexColor;
};
export const isValidText = (
    path: string,
    data = {},
    defaultData = {},
    regex?: RegExp
) => isValidValueInForm(path, data, defaultData, regex);

export const convertSecondsToPrometheusTime = (seconds: number) => {
    const units = { y: 31536000, w: 604800, d: 86400, h: 3600, m: 60, s: 1 };

    let secs = Number(seconds);

    if (secs <= 0) return "0s";

    let y = Math.floor(secs / units.y);
    let w = Math.floor((secs % units.y) / units.w);
    let d = Math.floor(((secs % units.y) % units.w) / units.d);
    let h = Math.floor((((secs % units.y) % units.w) % units.d) / units.h);
    let m = Math.floor(
        ((((secs % units.y) % units.w) % units.d) % units.h) / units.m
    );
    let s = Math.floor(
        ((((secs % units.y) % units.w) % units.d) % units.h) % units.m
    );

    let yDisplay = y > 0 ? `${y}y` : "";
    let wDisplay = w > 0 ? `${w}w` : "";
    let dDisplay = d > 0 ? `${d}d` : "";
    let hDisplay = h > 0 ? `${h}h` : "";
    let mDisplay = m > 0 ? `${m}m` : "";
    let sDisplay = s > 0 ? `${s}s` : "";
    return yDisplay + wDisplay + dDisplay + hDisplay + mDisplay + sDisplay;
};

export const convertPrometheusTimeToSeconds = (timeExpression: string) => {
    const units: any = {
        y: 31536000,
        w: 604800,
        d: 86400,
        h: 3600,
        m: 60,
        s: 1,
    };
    const regex = /(\d+)([ywdhms])?/g;

    let seconds = 0;
    let match;
    while ((match = regex.exec(timeExpression))) {
        seconds += Number.parseInt(match[1]) * units[match[2]];
    }

    return seconds;
};

export const padTimeFormat = (num: string) => {
    return (Number(num) < 10 ? "0" : "") + Number(num);
};

export const displaySensorType = (sensor: any) => {
    let name = get(sensor, "sensorType", sensor?.name);

    if (sensor?.app) {
        name = sensor.app[sensor.said].NAME;
    }
    return name ?? "-";
};

export const convertStringTo2dp = (str: string) => {
    return Number(str).toFixed(2);
};

export const convertSecondsToTimeString = (
    seconds: number,
    timeFormat: string
) => {
    const duration = moment.duration(seconds, "seconds");
    return moment.utc(duration.asMilliseconds()).format(timeFormat);
};

export const getLocalTimezone = (tz?: string) => {
    let timezone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timezone === "Asia/Saigon") {
        timezone = "Asia/Ho_Chi_Minh";
    }

    return timezone;
};

export const parseDateWithTimezone = (d: Moment, utcOffset: string) => {
    return moment(d.format(`YYYY-MM-DDTHH:mm:ss${utcOffset}`));
};

export const monthsToSeconds = (months: number) => {
    const ONE_MONTH_IN_SECONDS = 2592000;
    return ONE_MONTH_IN_SECONDS * months;
};

export const totalDoesNotExceedMax = ({
    total,
    numberOfChart,
}: {
    total: number;
    numberOfChart: number;
}) => {
    const maxSampleAllowed = 50000;
    const arbitraryGranularityValue = maxSampleAllowed / numberOfChart;
    return total <= arbitraryGranularityValue;
};
// Limits min/max by 1d.p
export const customMinMax = ({
    min,
    max,
    acc,
}: {
    min: number;
    max: number;
    acc: number;
}) => {
    const scale = 10 ** acc;
    const max_ = Math.floor(max * scale) / scale;
    const min_ = Math.ceil(min * scale) / scale;

    return {
        min: min_,
        max: max_,
    };
};

export const getAccuracyFromOBJ = ({
    object,
    jsonList,
    said,
    appId,
    isChart = true,
    isAggregated = false,
}: any) => {
    if (object) {
        let accuracy = undefined,
            format = "";
        const sensor = jsonList.find(
            ({ OBJ }: { OBJ: string }) => OBJ === object
        ).SNS[said];

        format = sensor?.FORMAT;

        if (sensor?.APPLICATION?.length) {
            const app = sensor.APPLICATION.find(({ APPID }: any) => {
                return APPID === appId;
            });
            const defaultUnit = getPrimaryUnitName(app);
            if (defaultUnit) {
                format = defaultUnit.FORMAT;
                accuracy = defaultUnit["ACCURACY"];
            }
        }

        accuracy =
            accuracy ?? get(sensor, "ACCURACY", sensor?.MODE?.[0]?.ACCURACY);

        if (isChart && isAggregated && format !== "float") {
            accuracy = "2";
        }

        return accuracy;
    }
};

export const handleAccuracyException = (accuracy: number) => {
    return accuracy > 0 ? accuracy - 1 : accuracy;
};

/**
 * Returns the exact accuracy value without any reduction.
 * Use this for Event Conditions where users should be able to enter
 * values with the full decimal precision defined in SNS/JSON ACCURACY field.
 *
 * @param accuracy - The accuracy value from SNS/JSON configuration
 * @returns The exact accuracy value (minimum 0)
 */
export const getExactAccuracy = (accuracy: number) => {
    return Math.max(accuracy, 0);
};

/**
 * Format a threshold value using accuracyException (1 dp less than full accuracy).
 *
 * Preserves raw value when accuracyException is 0 and value has decimals
 * (e.g. "26.5") to avoid 26.5→27 flicker: before sensor config loads,
 * accuracyException can be 0 (from ""), and addDecimalPlace(value, 0) would round.
 *
 * @param value - The value to format (number or string)
 * @param accuracyException - Reduced accuracy (accuracy - 1)
 * @returns Formatted string
 */
export const formatThresholdValue = (
    value: number | string,
    accuracyException: number
) => {
    const accException = Number(accuracyException);

    // accuracyException is invalid (e.g. undefined, NaN, or "" from getValueModeOrObj)
    if (!Number.isFinite(accException) || accException < 0) {
        return String(value);
    }
    const strVal = String(value);
    if (accException === 0 && /\.\d/.test(strVal)) {
        return strVal;
    }
    return addDecimalPlace(value, accException);
};

export const isInteger = (num: number | string) => {
    return Patterns.integerPattern.test(num + "");
};

export const isFloat = (num: number | string) => {
    return Patterns.float2DigitsPattern.test(num + "");
};

export const formattedChartValue = ({
    sensorAccuracy,
    value,
    useExponential = true,
}: {
    sensorAccuracy: any;
    value: any;
    useExponential?: boolean; // If true, the value will be truncated to the max length of the chart
}) => {
    let chartValue = addDecimalPlace(value, sensorAccuracy);

    if (!useExponential) {
        return chartValue;
    }

    // Reduce the value's length as there is a limit to the chart's width
    if (chartValue?.length > MAX_CHART_VALUE_CHARS) {
        //Note: this value can't be rounded

        const numValue = Number(chartValue);
        const absValue = Math.abs(numValue);
        const sign = numValue < 0 ? "-" : "";

        // Calculate the exponent
        // Math.log10(9999999999.999) ≈ 9.999999999999957
        // Math.floor(9.999999999999957) = 9
        const exponent = Math.floor(Math.log10(absValue));

        // Calculate the coefficient
        // 9999999999.999 / Math.pow(10, 9)
        // 9999999999.999 / 1000000000
        // 9.999999999999
        const coefficient = absValue / Math.pow(10, exponent);

        // Math.floor(9.999999999999 * 1000) = Math.floor(9999.999999999)
        // 9999 / 1000
        // 9.999
        const truncatedCoefficient = Math.floor(coefficient * 1000) / 1000;

        chartValue = `${sign}${truncatedCoefficient}e+${exponent}`;
    }

    return chartValue;
};

export const getYaxisDetails: any = (deviceInfo: {
    said: string;
    object: string;
    appId: string;
}) => {
    const { said, object, appId } = deviceInfo;

    if (!object) {
        return {
            yAxisMin: "",
            yAxisMax: "",
            dataFormat: "",
            isBinary: false,
        };
    }

    let yAxisMin = "";
    let yAxisMax = "";
    let dataFormat = "";

    const jsonList = LocalStorageService.getItem("listOfJSON");

    const device = jsonList.find(({ OBJ }: { OBJ: string }) => OBJ === object);
    const _device = device.SNS[said] ?? {};

    if (_device.APPLICATION) {
        const deviceApp = _device.APPLICATION.find(
            ({ APPID }: { APPID: string }) => APPID === appId
        );

        if (appId !== "na" && deviceApp) {
            const primaryUnit = getPrimaryUnitName(deviceApp);
            yAxisMin = primaryUnit?.MIN || "";
            yAxisMax = primaryUnit?.MAX || "";
            dataFormat = primaryUnit?.FORMAT || "";
        } else {
            // When appId is "na", use _device properties directly
            yAxisMin = _device.MIN || "";
            yAxisMax = _device.MAX || "";
            dataFormat = _device.FORMAT || "";
        }
    } else {
        yAxisMin = _device.MODE ? _device.MODE[0].MIN : _device.MIN;
        yAxisMax = _device.MODE ? _device.MODE[0].MAX : _device.MAX;
        dataFormat = _device.FORMAT;
    }

    return {
        yAxisMin,
        yAxisMax,
        dataFormat,
        isBinary: dataFormat === "boolean",
    };
};

export const getChartMinOrMax = ({
    chartSeriesInfo,
    value,
    type,
}: {
    chartSeriesInfo: any;
    value: any;
    type: "min" | "max";
}) => {
    const { min, max } = value;

    if (!isEmpty(chartSeriesInfo)) {
        const { yAxisMin = "", yAxisMax = "" } = chartSeriesInfo ?? {};
        if (type === "min") {
            return yAxisMin === "0" ? "0" : min;
        } else if (type === "max") {
            return yAxisMax === "1" ? "1" : max;
        }
    }
    return type === "min" ? min : max;
};

export const isIntegerChart = (format: any) => {
    return format === "integer";
};

export const isFourWiredMotorControllerApp = (obj: string, appId: string) => {
    const listFourWiredMotorObj = ["16385", "16386", "16387", "49152", "49155"];
    const listFourWiredMotorApp = ["32773", "32772"];
    return (
        listFourWiredMotorObj.includes(obj) &&
        listFourWiredMotorApp.includes(appId)
    );
};

export const getSensorFromDevices: any = (
    deviceList: any[],
    deviceInfo: { gateway_id: string; device_id: string; said: string }
) => {
    const { gateway_id, device_id, said } = deviceInfo;
    const sensorInfo = deviceList.find((d: any) => {
        const { SAID, ldsu_uuid, gatewayId } = d;
        if (
            gatewayId === gateway_id &&
            ldsu_uuid === device_id &&
            SAID === said
        ) {
            return d;
        }

        return null;
    });

    return sensorInfo || null;
};

/**
 * Based on the day, does not care about leap years
 * 02 Feb 2024 - 02 Feb 2025 >> Over 1 year (this is leap year)
 * 02 Feb 2024 - 01 Feb 2025 >> Not over 1 year
 */
export const isOverOneYear = (startDate: Date, endDate: Date) => {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);

    // Create a new date that's exactly one year after the start date
    const oneYearAfterStart = new Date(start);
    oneYearAfterStart.setFullYear(start.getFullYear() + 1);

    return end >= oneYearAfterStart;
};

export const getUniqueChartSeries = (
    chartSeries: {
        info: {
            name: string;
            unit: string;
            said: string;
            [key: string]: any;
        };
        [key: string]: any;
    }[]
) => {
    const uniqueSensors = uniqBy(chartSeries, (seriesItem: any) => {
        return [
            seriesItem?.info?.name?.trim() || "",
            seriesItem?.info?.unit || "",
            seriesItem?.info?.said || "",
        ].join("_");
    });

    return uniqueSensors;
};

/**
 * Calculate a text width by font family and size.
 *
 * @param {string} text - The text want to calculate.
 * @param {string} font - The font include font family and font size.
 * @returns {number} The width of text.
 * @throws {Error} Throws an error if the type does not match any known widget type.
 */
export const measureTextWidth = (text: string, font: string) => {
    const textMeasureCanvas = document.createElement("canvas");
    const ctx = textMeasureCanvas.getContext("2d");
    if (!ctx) return 32;
    ctx.font = font;
    const width = ctx.measureText(text).width;
    return width;
};

/**
 * Formats a value with its unit according to specific spacing rules
 * @param value The numeric value to format
 * @param unit The unit symbol
 * @returns Formatted string with proper spacing
 */
export const formatValueWithUnit = (
    value: string | number,
    unit: string
): string => {
    if (!unit) {
        return String(value);
    }

    // Units that don't need a space
    const noSpaceUnits = ["°C", "°F", "%", "C", "F"];

    // Time units that don't need a space
    const timeUnits = ["y", "w", "d", "h", "m", "s"];

    // Check if unit needs no space
    if (noSpaceUnits.includes(unit) || timeUnits.includes(unit)) {
        return `${value}${unit}`;
    }

    // All other units need a space
    return `${value} ${unit}`;
};

export const formatBinaryActuatorYAxisLabel = (
    value: string | number,
    sensorType: string,
    unit?: string
): string => {
    const numValue = Number(value);
    const lowerType = sensorType?.toLowerCase();

    const isBinaryActuator =
        lowerType?.includes("switch") || lowerType?.includes("digital output");

    if (!isBinaryActuator) {
        return formatValueWithUnit(value, unit ?? "");
    }

    // For binary actuators, only show 0 (Inactive) and 1 (Active)
    if (numValue === 1) {
        return "1 (Active)";
    } else if (numValue === 0) {
        return "0 (Inactive)";
    }

    // For any other values, round to nearest binary value
    return numValue >= 0.5 ? "1 (Active)" : "0 (Inactive)";
};

/**
 * Generates a random string of a given length
 * @param length The length of the string to generate
 * @returns A random string of the given length
 */
const chars = Array.from({ length: 36 }, (_, i) => i.toString(36)).join("");
export const getRandomString = (length: number = 13) => {
    const array = new Uint8Array(length);
    globalThis.crypto.getRandomValues(array);
    return Array.from(array, (x) => chars[x % 36]).join("");
};

/**
 * Calculate the increase or decrease percentage between current usage and previous usage
 * @param currentTotal
 * @param previousTotal
 */
export const calculatePercentage = (
    currentTotal: number | null | undefined,
    previousTotal: number | null | undefined
): NullableNumber => {
    const isCurrentInvalid =
        Number.isNaN(currentTotal) ||
        currentTotal === null ||
        currentTotal === undefined;
    const isPreviousInvalid =
        Number.isNaN(previousTotal) ||
        previousTotal === null ||
        previousTotal === undefined;

    if (isCurrentInvalid || isPreviousInvalid) {
        return null;
    }

    // Assuming calculations before this point to be accurate even when using IEEE 754 numbers
    // Converting to Decimal to handle edge cases (e.g. previous = 100, current = 105.05)
    const previous = new Decimal(previousTotal);
    const current = new Decimal(currentTotal);

    let percentage: Decimal;

    if (previous.equals(current)) {
        percentage = new Decimal(0);
    } else if (previous.equals(0) || current.equals(0)) {
        percentage = new Decimal(100);

        if (current.cmp(0) < 0 || previous.cmp(0) > 0) {
            percentage = percentage.mul(-1);
        }
    } else {
        // Normal case: calculate percentage difference
        // Using the original values without limiting decimal places for calculation
        percentage = current.sub(previous).div(previous.abs()).mul(100);
    }

    return percentage.toNumber();
};
